import classnames from 'classnames';
import { isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { FormattedMessage } from 'react-intl';

import { Button } from '../../atoms/button';
import { SearchField as SearchBar } from '../../misc/search-bar';
import { TableLoader } from '../table-loader';

import FilterSvg from '../../assets/icons/svgs/filter.svg';

import './table.scss';

class DataTable extends Component {
  /**
   * @param {data, columns} are required to render a table
   * @param {fetchFunction} is the main GET call to bring updated data after pagination actions
   * @param {format} is optional for custom display formats
   * @param {size} is required for pagination
   * @param {keyField} in case key field is different from "id"
   * @param {loading} is to show loader inside table
   * @param {searchFtn} only if search functionality is needed
   */

  static propTypes = {
    data: PropTypes.array.isRequired,
    size: PropTypes.number.isRequired,
    showPagination: PropTypes.bool,
    fetchFunction: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    customLimit: PropTypes.number.isRequired,
    customParams: PropTypes.object,
    customSizePerPageList: PropTypes.array,
    showDataSort: PropTypes.bool,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string.isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        width: PropTypes.string,
        sort: PropTypes.bool,
        format: PropTypes.func
      })
    ),
    searchFtn: PropTypes.func,
    setShowFilter: PropTypes.func,
    showClearBtn: PropTypes.bool,
    formatCells: PropTypes.func,
    keyField: PropTypes.string,
    selectRow: PropTypes.shape({
      mode: PropTypes.oneOf(['none', 'radio', 'checkbox']),
      onSelect: PropTypes.func,
      onSelectAll: PropTypes.func,
      columnWidth: PropTypes.string
    }),
    /** When false, first column sort click is descending, then ascending (react-bootstrap-table defaultASC). */
    defaultSortAscending: PropTypes.bool
  };

  static defaultProps = {
    defaultSortAscending: true
  };

  constructor(props) {
    super(props);

    this.state = {
      sortBy: undefined,
      sortOrder: 'asc',
      limit: this.props.customLimit || 10,
      page: 1,
      data: [],
      text: '',
      loading: props.loading || false,
      ...this.props.customParams
    };
    this._isMounted = false;
    this.onSortChange = this.onSortChange.bind(this);
    this.onSizePerPageList = this.onSizePerPageList.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.renderCustomFilterButton = this.renderCustomFilterButton.bind(this);
    this.updateState = this.updateState.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    if (this._isMounted) {
      this.setState({ data: this.props.data });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  componentDidUpdate(prevProps) {
    if (!this._isMounted) return;

    const { formatCells, customParams, customLimit } = this.props;
    formatCells?.();

    if (!isEqual(this.props.data, prevProps.data)) {
      this.setState({ data: this.props.data });
    }

    if (!isEqual(this.props.loading, prevProps.loading)) {
      this.setState({ loading: this.props.loading });
    }

    // Sync page and limit from parent component to keep state consistent
    if (customParams?.page !== undefined && customParams.page !== this.state.page) {
      this.setState({ page: customParams.page });
    }
  }

  updateState({ stateData, callback = () => null }) {
    if (!this._isMounted) return;
    this.setState(
      {
        ...stateData
      },
      callback
    );
  }

  dispatchStateValues = () => {
    // send parameter values to parent component to be used in fetch call
    const { sortBy, sortOrder, limit, page, text } = this.state;
    let stateData = { limit, page, [sortOrder]: sortBy };
    stateData = {
      // add text in state if it exists ie search is enabled
      ...(text && { text: text }),
      ...stateData
    };
    return stateData;
  };

  onSortChange = (sortName, sortOrder) => {
    if (!this._isMounted) return;
    this.setState(
      {
        sortBy: sortName,
        sortOrder
      },
      () => {
        if (!this._isMounted) return;
        const { fetchFunction } = this.props;
        fetchFunction(this.dispatchStateValues());
      }
    );
  };

  onSizePerPageList = (sizePerPage, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this._isMounted) return;
    this.setState(
      {
        limit: sizePerPage,
        page: 1 // Reset to page 1 when limit changes to prevent double fetch
      },
      () => {
        if (!this._isMounted) return;
        const { fetchFunction } = this.props;
        fetchFunction(this.dispatchStateValues());
      }
    );
  };

  onPageChange = (page, sizePerPage) => {
    if (!this._isMounted) return;
    const { limit: currentLimit } = this.state;
    const limitChanged = currentLimit !== sizePerPage;

    this.setState(
      {
        page,
        limit: sizePerPage
      },
      () => {
        if (!this._isMounted) return;
        // If limit changed, onSizePerPageList already handled the fetch
        // Skip fetch here to prevent double call
        if (!limitChanged) {
          const { fetchFunction } = this.props;
          fetchFunction(this.dispatchStateValues());
        }
      }
    );
  };

  formatCell = (cell) => {
    return <span className="table-text">{cell || 'N/A'}</span>;
  };

  renderCustomClearSearch = () => {
    return (
      <Button
        onClick={() => {
          if (!this._isMounted) return;
          clearTimeout(this.timer);
          this.setState(
            {
              text: ''
            },
            () => {
              if (!this._isMounted) return;
              this.timer = setTimeout(this.getSearchData, 1000);
            }
          );
        }}
        text={'Clear'}
      />
    );
  };

  renderCustomFilterButton = (setShowFilter) => {
    return (
      <Button
        onClick={() => setShowFilter((prev) => !prev)}
        text={<img src={FilterSvg} alt="filter" />}
        style={{ backgroundColor: '#E6F4FF', width: '36px', height: '42px', maxHeight: '42px' }}
      />
    );
  };

  handleSearchChange = (searchText = '') => {
    if (!this._isMounted) return;
    const text = searchText;
    clearTimeout(this.timer);
    this.setState(
      {
        text: text
      },
      () => {
        if (!this._isMounted) return;
        this.timer = setTimeout(this.getSearchData, 1000);
      }
    );
  };

  getSearchData = () => {
    if (!this._isMounted) return;
    const { sortBy, sortOrder, text, limit } = this.state;
    const { searchFtn } = this.props;
    let data = { text: text.trim(), page: 1, limit: limit };

    if (sortOrder) {
      data = {
        text: text.trim(),
        page: 1,
        limit: limit,
        [sortOrder]: sortBy
      };
    }

    this.setState({ page: 1 }, () => {
      if (!this._isMounted) return;
      searchFtn(data);
    });
  };

  setLoadingText = () => {
    const { loading } = this.state;
    const { data } = this.props;
    if (loading) {
      return (
        <div
          style={{
            display: 'flex',
            alignItem: 'center',
            position: 'absolute',
            left: '55%'
          }}
        >
          <TableLoader />
        </div>
      );
    }
    if (isEmpty(data)) {
      // If data is empty then show the message instead of loader
      return <FormattedMessage id="error.no_data_to_display" />;
    }
    return null;
  };

  displayTotal = (start, to, total) => {
    const { hideTableCount } = this.props;
    return (
      <div
        className="total"
        style={{
          visibility: hideTableCount === true ? 'hidden' : 'visible'
        }}
      >
        <p>Total: {total}</p>
      </div>
    );
  };

  render() {
    const {
      columns,
      size,
      keyField,
      searchFtn,
      showClearBtn = true,
      searchFieldPH,
      filterComponent = () => null,
      onRowMouseOver = () => null,
      onRowMouseOut = () => null,
      showPagination = true,
      selectRow,
      showDataSort = true,
      data = {},
      defaultSortAscending
    } = this.props;
    const { sortBy, sortOrder, limit, page, loading, text = '' } = this.state;

    const allowSearch = typeof searchFtn === 'function';
    // add Opacity and change color of column when loading
    let opacityClass = classnames({ 'table-column--custom': loading });

    let options = {
      sortName: sortBy,
      sortOrder,
      onSortChange: this.onSortChange,
      onSizePerPageList: this.onSizePerPageList,
      onPageChange: this.onPageChange,
      loading: loading,
      noDataText: this.setLoadingText(),
      page: page,
      sizePerPageList: this.props.customSizePerPageList || [10, 25, 30, 50],
      sizePerPage: limit,
      prePage: '←',
      nextPage: '→',
      paginationShowsTotal: this.displayTotal,
      onRowMouseOver,
      onRowMouseOut
    };

    if (!showClearBtn && allowSearch) {
      const { loading, setShowFilter } = this.props;
      options = {
        ...options,
        searchField: (props) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '0px 8px' }}>
            <div style={{ display: 'flex', width: '100%' }}>
              <SearchBar
                {...props}
                defaultValue={text}
                placeholder={searchFieldPH || 'Search'}
                isLoading={loading}
                onChange={this.handleSearchChange}
                style={{ width: '100%' }}
              />
              {this.renderCustomFilterButton(setShowFilter)}
            </div>
            {filterComponent()}
          </div>
        ),
        clearSearchBtn: null,
        clearSearch: false
      };
    }

    if (showClearBtn && allowSearch) {
      const { loading, setShowFilter } = this.props;
      options = {
        ...options,
        searchField: (props) => (
          <div
            style={{
              margin: '0px 8px'
            }}
          >
            <SearchBar
              {...props}
              defaultValue={text}
              placeholder={searchFieldPH || 'Search'}
              isLoading={loading}
              onChange={this.handleSearchChange}
              style={{ width: '100%' }}
            />
          </div>
        ),
        clearSearchBtn: this.renderCustomClearSearch,
        clearSearch: true
      };
    }

    return (
      <div className={`custom-table ${this.state.customClassName}`}>
        <BootstrapTable
          data={data}
          options={options}
          hover={false}
          selectRow={selectRow}
          pagination={showPagination}
          remote={true}
          fetchInfo={{ dataTotalSize: size }}
          bordered={false}
          striped={false}
          tableHeaderClass="table-header"
          trClassName="table-row"
          bodyContainerClass="table-body"
          search={allowSearch}
          keyField={keyField || 'id'}
        >
          {columns?.map((col) => {
              return (
                <TableHeaderColumn
                  key={col['field']}
                  dataSort={showDataSort && col['sort']}
                  width={col['width']}
                  dataField={col['field']}
                  className={opacityClass}
                  dataFormat={col['format'] ? col['format'] : this.formatCell}
                  defaultASC={col.defaultASC ?? defaultSortAscending}
                  {...{
                    ...(col['tdStyle'] && {
                      tdStyle: col['tdStyle'],
                    }),
                    ...(col['thStyle'] && {
                      thStyle: col['thStyle'],
                    }),
                    ...(col['caretRender'] && {
                      caretRender: col['caretRender'],
                    }),
                    ...(col['row'] && {
                      row: col['row'],
                    }),
                    ...(col['colSpan'] && {
                      colSpan: col['colSpan'],
                    }),
                    ...(col['rowSpan'] && {
                      colSpan: col['rowSpan'],
                    }),
                  }}
                >
                  {col['label'] || ''}
                </TableHeaderColumn>
              );
            })}
        </BootstrapTable>
      </div>
    );
  }
}

export { DataTable };
